<template>
    <pendo-popover-v2
        ref="popoverRef"
        :title="$t('sideNav.recents.Recents')"
        :placement="popoverPlacement"
        :popper-options="popperOptions"
        :distance="popoverOffset"
        :popper-class="['recents-popover', { 'is-empty': isEmpty }]"
        :hover="false"
        :container="false"
        keyboard-navigation
        :delay="{ show: 0, hide: 0 }"
        @show="onPopoverShow"
        @hide="onPopoverClose">
        <template #default="{ shown }">
            <recents-nav-item
                ref="recentsTrigger"
                data-cy="recents-trigger"
                :expanded="expanded"
                :is-open="shown"
                pendo-analytics="recents-nav-item"
                @mouseenter="$emit('mouseenter', 'recents')"
                @mouseleave="$emit('mouseleave')" />
        </template>
        <template #popover>
            <div
                ref="recentsPopoverContent"
                data-cy="recents-popover"
                class="recents-popover__content-wrapper"
                @mouseenter="$emit('mouseenter', 'recents')"
                @mouseleave="$emit('mouseleave')">
                <div
                    v-if="isEmpty"
                    class="recents-empty-state">
                    <div class="recents-empty-state__title">
                        {{ $t('sideNav.recents.No recent items yet') }}
                    </div>
                    <div class="recents-empty-state__subtitle">
                        {{ $t('sideNav.recents.Start using the product and your recent items will show up here.') }}
                    </div>
                </div>
                <pendo-loading-indicator
                    v-if="loading"
                    type="skeleton"
                    data-cy="recents-loading-skeleton-header"
                    class="recents-popover__loading header-loading"
                    :skeleton-props="{
                        height: 16,
                        width: 39,
                        borderRadius: 3,
                    }" />
                <h2
                    v-if="recents.length > 0 && !loading"
                    class="side-nav-option-popover__title">
                    {{ $t('sideNav.recents.Recents') }}
                </h2>
                <div class="recents-popover__body">
                    <div
                        v-for="n in 5"
                        :key="n"
                        class="recents-skeleton--item">
                        <pendo-loading-indicator
                            v-if="loading"
                            type="skeleton"
                            data-cy="recents-loading-skeleton-body"
                            class="recents-popover__loading item-loading"
                            :skeleton-props="{
                                height: 29,
                                width: 285,
                                borderRadius: 5,
                            }" />
                    </div>
                    <div
                        v-if="recents.length > 0 && !loading"
                        ref="recentsList">
                        <ul class="recents-list">
                            <li
                                v-for="(item, index) in recentsWithStars"
                                :key="item.id"
                                v-pendo-tooltip="{
                                    content: tooltipContent,
                                    placement: 'right',
                                    arrow: true,
                                    delay: { show: 1000, hide: 0 },
                                }"
                                class="recents-list__item"
                                :class="{ 'recents-list__item--active': activeItem === index }"
                                @mouseenter="activeItem = index"
                                @focus="activeItem = index">
                                <pendo-router-link
                                    :ref="`recents-list-item-${index}`"
                                    class="recents-list__link"
                                    :to="item.route"
                                    @mouseover="onMouseover(item)"
                                    @focus="activeItem = index"
                                    @click="handleItemClick($event, index)"
                                    @keydown.enter="handleItemClick($event,index)"
                                    @keydown.space="handleItemClick($event,index)">
                                    <pendo-icon
                                        class="recents-list__prefix-icon"
                                        :type="item.icon.name"
                                        size="16" />
                                    <div
                                        :ref="`recent_${item.id}`"
                                        class="recents-list__label">
                                        {{ item.name }}
                                    </div>
                                </pendo-router-link>
                                <star
                                    v-show="(index === activeItem || item.starred) && canStar(item.type)"
                                    :id="item.id"
                                    v-pendo-tooltip="{
                                        content: $t('sideNav.recents.Favorite'),
                                        placement: 'top',
                                    }"
                                    :class="{ 'recents-list__star': canStar(item.type) }"
                                    :tabindex="canStar(item.type) ? 0 : -1"
                                    :type="item.type" />
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import { computed, ref } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { itemHistoryAndFavoritesWithSummaries } from '@/homepage/homepage.utils.js';
import { hasOEMProductAnalyticsRedirect } from '@/vue-router/route-permissions.utils';
import uniqBy from 'lodash/uniqBy';
import { elementHasOverflowX, getFirstFocusableNode } from '@/utils/dom.utils';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils';

import { PendoPopoverV2, PendoIcon, PendoTooltip, PendoLoadingIndicator, PendoNotification } from '@pendo/components';
import Star from '@/common/star/star';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import RecentsNavItem from '@/side-nav/components/top-level-nav-items/recents-nav-item.vue';

export default {
    name: 'RecentsSubNav',
    components: {
        PendoPopoverV2,
        PendoIcon,
        Star,
        PendoLoadingIndicator,
        PendoRouterLink,
        RecentsNavItem,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        openPopover: {
            type: String,
            default: '',
        },
        expanded: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['handlePopover', 'mouseenter', 'mouseleave'],
    setup () {
        const recentsList = ref(null);
        const { width: windowWidth } = useWindowSize();

        const isSmallScreen = computed(() => windowWidth.value < 576);

        const popoverPlacement = computed(() => {
            return isSmallScreen.value ? 'bottom-start' : 'right-start';
        });

        const popperOptions = computed(() => {
            if (isSmallScreen.value) {
                return {
                    modifiers: {
                        flip: { enabled: false },
                        preventOverflow: { enabled: false },
                    },
                };
            }

            return {};
        });

        const popoverOffset = computed(() => {
            return isSmallScreen.value ? '0' : '8';
        });

        return {
            popperOptions,
            popoverPlacement,
            popoverOffset,
            recentsList,
        };
    },
    data () {
        return {
            activeItem: null,
            recents: [],
            loading: true,
            tooltipContent: null,
        };
    },
    computed: {
        ...mapGetters({
            allItemHistory: 'userSettings/history/allItemHistory',
            isStarredById: 'stars/isStarredById',
        }),
        recentsWithStars () {
            return this.recents.map((item) => ({ ...item, starred: this.isStarredById(item.id) }));
        },
        isEmpty () {
            return this.recents.length === 0 && !this.loading;
        },
        showRecentsPopover () {
            return this.openPopover === 'recents';
        },
    },
    watch: {
        showRecentsPopover: {
            async handler (showPopover) {
                await this.$nextTick();
                const ref = this.$refs.popoverRef;
                if (showPopover) {
                    if (!ref?.isShown) {
                        ref?.show?.();
                    }
                } else if (ref?.isShown) {
                    ref.hide?.();
                }
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions({
            loadAllStars: 'stars/loadAll',
        }),
        canStar (itemType) {
            return itemType !== 'VocItem';
        },
        onMouseover (item) {
            const labelRefName = `recent_${item.id}`;
            const { clientWidth, scrollWidth } = this.$refs[labelRefName][0] || {};
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = item.name;
            } else {
                this.tooltipContent = null;
            }
        },
        async onPopoverShow () {
            this.$emit('handlePopover', 'recents');
            this.activeItem = 0;
            this.loading = true;

            await this.loadAllStars();
            const viewedHistoryTypes = Object.values(HISTORY_TYPES);
            const allItemHistory = uniqBy(this.allItemHistory(viewedHistoryTypes), 'id');
            try {
                const { recents } = await itemHistoryAndFavoritesWithSummaries(allItemHistory);
                let recentsList = recents;
                if (hasOEMProductAnalyticsRedirect()) {
                    const hiddenTypes = ['Page', 'Feature', 'TrackType'];
                    recentsList = recentsList.filter((item) => !hiddenTypes.includes(item.type));
                }
                this.recents = recentsList.splice(0, 10);
                this.loading = false;

                if (this.recents.length > 0) {
                    this.$nextTick(() => {
                        const firstFocusableNode = getFirstFocusableNode(this.$refs.recentsPopoverContent);
                        if (firstFocusableNode) {
                            firstFocusableNode.focus();
                        }
                    });
                }
            } catch (error) {
                PendoNotification({
                    type: 'error',
                    title: this.$t('sideNav.recents.Something went wrong'),
                    message: this.$t('sideNav.recents.There was an issue loading recents. Please try again.'),
                });
                this.$refs.popoverRef?.hide?.();
                throw error;
            }
        },
        onPopoverClose () {
            if (this.openPopover === 'recents' || !this.openPopover) {
                this.$emit('handlePopover');
            }
        },
        handleItemClick ($event, index) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            if ($event.type === 'keydown') {
                this.$refs[`recents-list-item-${index}`]?.[0]?.$el?.click();
            }

            this.$refs.popoverRef?.hide?.();
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.recents-popover__content-wrapper {
    padding: 12px 0px 6px 0px;
    height: 100%;
    width: 100%;
    border-radius: 4px;

    &:focus-visible {
        outline: 2px solid var(--palette-gray-90);
        outline-offset: -1px;
    }
}

.recents-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 100%;
    padding: 0 32px;

    &__title {
        font-weight: 600;
        font-size: 14px;
        color: var(--palette-gray-100);
    }

    &__subtitle {
        font-size: 12px;
        font-weight: 400;
        text-align: center;
        color: var(--palette-gray-70);
    }
}

.recents-popover__loading {
    padding: 10px 0px 10px 16px;
}

.header-loading {
    padding-top: 4px;
}

.item-loading {
    padding-bottom: 16px;
}

.side-nav-option-popover__title {
    padding-left: 16px !important;
    margin: 0 0 8px 0 !important;
}

.recents-list {
    list-style-type: none;
    padding: 0px;

    &__item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 16px 0 12px;

        &:hover,
        &--active {
            background-color: var(--palette-gray-20);
        }
    }

    &__link {
        height: 29px;
        cursor: pointer;
        display: flex;
        flex-grow: 1;
        min-width: 0;
        align-items: center;
        gap: 8px;
        text-decoration: none;
        color: var(--palette-gray-100);
        padding: 0 8px;
        border-radius: 3px;

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }
    }

    &__label {
        font-size: 14px;
        font-weight: 400;
        flex-grow: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__star {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 6px !important;
        background-color: transparent;
        border: none;
        transition: none;

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }
    }
}
</style>

<style lang="less">
.recents-popover {
    .pendo-popover-v2__panel {
        width: 319px;
        height: 343px;
        padding: 0;
        border-radius: 4px;
        display: flex;
    }

    &.is-empty .pendo-popover-v2__panel {
        justify-content: center;
        align-items: center;
    }
}
</style>
